import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { PageContent, PageTheme } from '../model/pages';
import type { PreviewForm } from '../render/FormMock';
import PageView from '../render/PageView';
import IframePreview from './IframePreview';

// Render the page at a desktop-ish width, then scale the whole thing down to
// fit the card. This gives a real, pixel-accurate "screenshot" of the page (the
// same public render tree, never stale) instead of a CSS skeleton.
const VIRTUAL_WIDTH = 1024;

/** A live miniature of a landing page or template: the actual PageView rendered
 *  in a same-origin frame that carries the published pages' stylesheet (so the
 *  breakpoints evaluate at the desktop width and the staff app's CSS never
 *  reaches it), scaled down to the card. Mounts when it scrolls near the
 *  viewport and shows `fallback` (the cheap CSS skeleton) until then, so a list
 *  of 20+ pages doesn't build 20 live frames at once. Decorative: it is inert
 *  (no focus, no clicks), so a click lands on the surrounding card. */
export default function TemplatePreview({ content, theme, height = 168, fallback, previewForm = null, flush, title = 'Page preview' }: {
  content: PageContent;
  theme: PageTheme;
  height?: number;
  fallback?: ReactNode;
  /** A still of the form (FormMock) where the page's Form section goes, so it
   *  shows real fields instead of an empty card. No live form frame is loaded. */
  previewForm?: PreviewForm | null;
  /** Edge to edge inside a card (the card owns the border and corners). */
  flush?: boolean;
  /** The frame's accessible name. */
  title?: string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [inView, setInView] = useState(false);

  // Measure the card's real width so the virtual page scales into it.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    if (typeof ResizeObserver === 'undefined') { setWidth(el.clientWidth); return; }
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Build the live frame only when the card scrolls near the viewport.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); }
    }, { rootMargin: '300px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const scale = width ? width / VIRTUAL_WIDTH : 0;
  const ready = inView && scale > 0;
  // Gallery name/search/filter changes do not change the page. Keep the heavy
  // render tree stable even when the card's fallback or outer sizing changes.
  const page = useMemo(() => <PageView content={content} theme={theme} previewForm={previewForm} preview />, [content, theme, previewForm]);

  return (
    <div ref={wrapRef} aria-hidden inert data-template-preview
      className={`relative w-full overflow-hidden bg-surface2 ${flush ? '' : 'rounded-lg border border-border'}`}
      style={{ height }}>
      {ready ? (
        <IframePreview title={title}
          style={{
            width: VIRTUAL_WIDTH,
            height: Math.ceil(height / scale),
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            pointerEvents: 'none',
          }}>
          {page}
        </IframePreview>
      ) : (fallback ?? null)}
    </div>
  );
}
